import { typography, radius, semantic } from '@/shared/theme/tokens';
import { LIVE_STATUS_META, withAlpha } from '../../liveModeMeta';
import { MONO } from './recommendedTheme';

const TICKER_CSS = `.rec-ticker-track:hover { animation-play-state: paused; }`;
const durationMs = (n) => Math.max(18, n * 6) * 1000;

/**
 * Live Alerts crawl — the News-channel bottom ticker, theme-aware. One scrolling
 * line of PARAMETER · STATUS · VALUE; stale items grey out and read "——". The
 * crawl is the only ambient motion (gated on `animate`; static under reduced-
 * motion). The alert that just changed (`flashId`) flashes once. Each segment is
 * a click target → onSelect(alert).
 */
export function AlertTicker({ alerts = [], flashId, animate, c, onSelect }) {
  const clickable = typeof onSelect === 'function';
  return (
    <div style={{
      flexShrink: 0, display: 'flex', alignItems: 'stretch', height: 48,
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.lg, overflow: 'hidden',
    }}>
      <style>{TICKER_CSS}</style>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', background: withAlpha(semantic.error, '16'), borderRight: `1px solid ${c.border}` }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: semantic.error }}>notifications_active</span>
        <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color: semantic.error }}>
          Live Alerts
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {alerts.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: semantic.successAlt }}>check_circle</span>
            <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: c.textMuted, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase' }}>
              All parameters in range
            </span>
          </div>
        ) : animate ? (
          <div className="rec-ticker-track" style={{ display: 'flex', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap', willChange: 'transform', animation: `rec-marquee ${durationMs(alerts.length)}ms linear infinite` }}>
            <Segments alerts={alerts} flashId={flashId} clickable={clickable} onSelect={onSelect} c={c} />
            <Segments alerts={alerts} flashId={flashId} clickable={clickable} onSelect={onSelect} c={c} ariaHidden />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <Segments alerts={alerts} flashId={flashId} clickable={clickable} onSelect={onSelect} c={c} />
          </div>
        )}
      </div>
    </div>
  );
}

function Segments({ alerts, flashId, clickable, onSelect, c, ariaHidden }) {
  return (
    <div aria-hidden={ariaHidden || undefined} style={{ display: 'flex', alignItems: 'center' }}>
      {alerts.map((a) => {
        const stale = !!a.isStale;
        const meta = LIVE_STATUS_META[a.status] ?? LIVE_STATUS_META.ok;
        const color = stale ? c.textSubtle : meta.color;
        const flashing = !ariaHidden && !stale && a.id === flashId;
        // The duplicated (aria-hidden) marquee copy is purely visual — not a click
        // target, not focusable, no title (so it never double-matches by label).
        const interactive = clickable && !ariaHidden;
        return (
          <div
            key={`${ariaHidden ? 'b' : 'a'}-${a.id}`}
            onClick={interactive ? () => onSelect(a) : undefined}
            onKeyDown={interactive ? (e) => { if (e.key === 'Enter') onSelect(a); } : undefined}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            title={interactive ? `Open ${a.parameterName} alert` : undefined}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, padding: '0 16px', height: 48,
              borderRight: `1px solid ${c.border}`, background: flashing ? withAlpha(color, '2e') : 'transparent',
              opacity: stale ? 0.6 : 1, cursor: interactive ? 'pointer' : 'default',
              pointerEvents: ariaHidden ? 'none' : undefined, transition: 'background 0.5s ease',
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: radius.full, background: color, flexShrink: 0, boxShadow: flashing ? `0 0 8px ${color}` : 'none' }} />
            <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color: c.textPrimary }}>
              {a.parameterName}
            </span>
            <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color }}>
              {stale ? 'STALE' : meta.label}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3 }}>
              <span style={{ fontFamily: MONO, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color, fontVariantNumeric: 'tabular-nums' }}>
                {stale ? '——' : a.liveValue}
              </span>
              {a.unit && !stale && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle }}>{a.unit}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}
